import { inject, observer } from 'mobx-react';
import React from 'react';

import { Button, Checkbox, Dialog, FormControlLabel, TextField, Typography } from '@mui/material';
import { lightGreen, orange, red, yellow } from '@mui/material/colors';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import API from 'app/API';
import Hubspot from 'app/main/common/Hubspot';

import Captcha from './../utils/Captcha';

const red600 = red['600'];
const orange500 = orange['500'];
const yellow500 = yellow['500'];
const lightGreenA400 = lightGreen.A400;

const colorsCriteria = [
    { color: lightGreenA400, ln: 0.8 },
    { color: yellow500, ln: 0.6 },
    { color: orange500, ln: 0.4 },
    { color: red600, ln: 0.0 }
];

const recaptchaRef = React.createRef();

@inject('userStore')
@inject('dialogStore')
@observer
export default class SignUp extends React.Component {
    state = {
        email: '',
        password: '',
        repeatPassword: '',
        marketingAgreement: false,
        eula: true,

        errorText: '',
        emailError: '',
        successText: '',
        passwordError: '',
        passwordRepeatError: '',
        passwordColor: lightGreenA400,
        repeatPasswordColor: lightGreenA400
    };

    checkPassRepeat = (event) => {
        const pValue = this.state.password;
        const rpValue = event.target.value;
        this.setState({
            passwordRepeatError: rpValue === pValue ? '' : 'Passwords are different',
            passwordRepeatUnderlineStyleColor: rpValue === pValue ? lightGreenA400 : red600
        });
    };

    /**
     * @Check pass strength
     */
    checkPassStrength = () =>
        API.fetch(API.CHECK_PASSWORD, {
            password: this.state.password
        }).then((response) => {
            const errors = Object.keys(response.data.improvements).map((k) => response.data.improvements[k]);
            this.setState({
                passwordError: response.data.strenght > 0.5 ? '' : errors[0],
                passwordColor: colorsCriteria.filter((s) => response.data.strenght >= s.ln)[0].color
            });
        });

    /**
     * @Check email in use
     */
    checkEmail = () =>
        API.fetch(API.CHECK_EMAIL, {
            email: this.state.email
        }).then((response) => {
            this.setState({
                emailError: response.result === 'OK' ? '' : response.data.error
            });
        });

    handleChange = (e) => this.setState({ [e.target.name]: e.target.value });

    handleMarketingAgreementChange = () => {
        this.setState({ marketingAgreement: !this.state.marketingAgreement });
    };

    handleEulaChange = () => {
        this.setState({ eula: !this.state.eula });
    };

    signUp = () => {
        const { email, password, marketingAgreement } = this.state;

        API.fetchWithToken(API.ADD, {
            email,
            password,
            marketingAgreement: +marketingAgreement,
            captcha: recaptchaRef.current.getValue()
        }).then((response) => {
            this.setState({
                errorText: response.result === 'error' ? response.data.error : '',
                successText: response.result === 'OK' ? 'User has been registered' : ''
            });

            if (response.result === 'OK') {
                Hubspot.send(Hubspot.SIGNUP, { email });
                if (marketingAgreement) {
                    Hubspot.send(Hubspot.MARKETING_AGREEMENT, { email });
                }

                this.props.dialogStore.openMessageDialog(
                    'Activate your account',
                    'Message with account activation instructions was sent to your email'
                );
            } else if (response.result === 'error') {
                recaptchaRef.current.recoverCaptchaId();
            }
        });
    };

    render() {
        const { dialogStore } = this.props;
        const { openLoginDialog, signupDialogOpen } = dialogStore;

        return (
            <Dialog maxWidth="xs" open={signupDialogOpen} className="vulners-dialog" onClose={openLoginDialog}>
                <DialogTitle>Sign Up to Vulners</DialogTitle>
                <DialogContent>
                    <TextField fullWidth name="email" label="Login" onChange={this.handleChange} />
                    <br />
                    <TextField
                        fullWidth
                        name="email"
                        label="Email"
                        onBlur={this.checkEmail}
                        onChange={this.handleChange}
                        error={!!this.state.emailError}
                        helperText={this.state.emailError}
                    />
                    <br />
                    <TextField
                        fullWidth
                        name="password"
                        type="password"
                        label="Password"
                        onChange={this.handleChange}
                        onBlur={this.checkPassStrength}
                        error={!!this.state.passwordError}
                        helperText={this.state.passwordError}
                    />
                    <br />
                    <TextField
                        fullWidth
                        type="password"
                        name="repeatPassword"
                        label="Repeat Password"
                        onChange={(e) => {
                            this.handleChange(e);
                            this.checkPassRepeat(e);
                        }}
                        onBlur={this.handleChange}
                        error={!!this.state.passwordRepeatError}
                        helperText={this.state.passwordRepeatError}
                    />
                    <br />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={this.state.eula}
                                onChange={this.handleEulaChange}
                                name="elua"
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body2">
                                I agree to the Vulners{' '}
                                <Typography
                                    variant="body2"
                                    gutterBottom
                                    component="a"
                                    color="primary"
                                    href="static/docs/eula.pdf"
                                    target="_blank"
                                >
                                    End of User Licence Agreement
                                </Typography>
                            </Typography>
                        }
                    />
                    <br />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={this.state.marketingAgreement}
                                onChange={this.handleMarketingAgreementChange}
                                name="marketingAgreement"
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body2">
                                I would like to receive newsletter in accordance with our{' '}
                                <Typography
                                    variant="body2"
                                    gutterBottom
                                    component="a"
                                    color="primary"
                                    href="/static/docs/privacy_policy.html"
                                    target="_blank"
                                >
                                    Privacy Policy
                                </Typography>
                            </Typography>
                        }
                    />
                    <br />
                    <Captcha id="vulners-sign-up-captcha" ref={recaptchaRef} />

                    <div className="vulners-dialog-error-text">{this.state.errorText}</div>
                    <div className="vulners-dialog-success-text">{this.state.successText}</div>

                    <Button
                        disabled={!this.state.eula}
                        fullWidth
                        color="primary"
                        variant="contained"
                        onClick={this.signUp}
                    >
                        Sign Up
                    </Button>

                    <br />
                    <br />
                </DialogContent>
            </Dialog>
        );
    }
}
